import { useAuthStore } from '../stores/authStore';
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
// Paths that should NEVER trigger auth refresh or logout
const PUBLIC_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password', '/health', '/site-config/branding', '/site-config/emergency', '/schools/search'];
let isRefreshing = false;
async function request(path, options = {}) {
    const isPublic = PUBLIC_PATHS.some(p => path.startsWith(p));
    // Read token directly from localStorage — Zustand store may not have rehydrated after page reload
    let token = localStorage.getItem('koggelman_access_token') || useAuthStore.getState().accessToken;
    const lang = localStorage.getItem('koggelman_lang') || 'en';
    const headers = {
        'Accept-Language': lang,
        ...(token && { Authorization: `Bearer ${token}` }),
    };
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: { ...headers, ...(options.headers || {}) },
    });
    // For public paths (login, register), never intercept — just return the response
    if (isPublic) {
        const json = await response.json();
        if (!response.ok)
            throw new Error(json.error?.message || 'Request failed');
        return json;
    }
    // For protected paths, handle 401
    if (response.status === 401 && !isRefreshing) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            // Retry with new token from localStorage
            const newToken = localStorage.getItem('koggelman_access_token');
            headers['Authorization'] = `Bearer ${newToken}`;
            const retryResponse = await fetch(`${BASE_URL}${path}`, {
                ...options,
                headers: { ...headers, ...(options.headers || {}) },
            });
            const retryJson = await retryResponse.json();
            if (!retryResponse.ok)
                throw new Error(retryJson.error?.message || 'Request failed');
            return retryJson;
        }
        // Refresh failed — force re-login
        forceLogout();
        throw new Error('Session expired');
    }
    const json = await response.json();
    if (!response.ok)
        throw new Error(json.error?.message || 'Request failed');
    return json;
}
async function refreshAccessToken() {
    if (isRefreshing)
        return false;
    isRefreshing = true;
    const rt = localStorage.getItem('koggelman_refresh_token') || useAuthStore.getState().refreshToken;
    if (!rt) {
        isRefreshing = false;
        return false;
    }
    try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) {
            isRefreshing = false;
            return false;
        }
        const json = await res.json();
        if (json.data?.accessToken) {
            const store = useAuthStore.getState();
            store.setAuth(store.user, json.data.accessToken, json.data.refreshToken || rt);
            isRefreshing = false;
            return true;
        }
        isRefreshing = false;
        return false;
    }
    catch {
        isRefreshing = false;
        return false;
    }
}
function forceLogout() {
    useAuthStore.getState().logout();
    if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
    }
}
export const api = {
    get: (path) => request(path),
    post: (path, body) => request(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
    patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (path) => request(path, { method: 'DELETE' }),
    upload: (path, formData) => request(path, { method: 'POST', body: formData }),
};
